import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildHowToStepsSchema,
  buildSpeakableSchema,
} from "@/lib/schema";
import { POA_FAQ_CATEGORIES } from "@/content/poa-corpus/faq-defs";
import { POA_JURISDICTION_NAMES as POA_JURISDICTIONS } from "@/content/poa-corpus/names.gen";

const STEPS = [
  {
    title: "Define the powers against the actual transaction",
    body: "Name the principal and attorney-in-fact, quote the title deed or account number, and list each permitted act. Thai authorities read a power of attorney strictly under Civil and Commercial Code sections 797-828.",
  },
  {
    title: "Use the form the receiving authority mandates",
    body: "Land transactions require Land Department form Tor Dor 21, company filings use the DBD format, and banks or estate administrators often insist on their own template. Confirm the form before anyone signs.",
  },
  {
    title: "Sign before a Notarial Services Attorney",
    body: "A lawyer registered with the Lawyers Council of Thailand certifies the signature, the signer's identity and free consent, and records the act in a register that can be verified later.",
  },
  {
    title: "Affix stamp duty and add a certified translation",
    body: "Stamp duty follows the Revenue Department schedule. For cross-border use, add a certified translation in the format the receiving authority accepts before legalization starts.",
  },
  {
    title: "Legalize at MFA and the destination embassy, or use an Apostille",
    body: "File at the Legalization Division, Department of Consular Affairs: THB 200 per stamp standard or THB 400 express, then the destination embassy in Bangkok. From 28 February 2027 Hague Convention states accept a single Apostille instead.",
  },
];

export const Route = createFileRoute("/en/poa/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/poa-corpus/faq-en");
    return { faqs: m.POA_FAQ_EN.slice(0, 12) };
  },
  component: PoaHubEn,
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const faqs = loaderData?.faqs ?? [];
    return {
      ...pageHeadForProvince({
        title: "Thai Power of Attorney — Forms, Notarization & Destination Rules",
        description:
          "Hub for cross-border powers of attorney from Thailand: mandatory forms for land, banking, estates and companies, stamp duty, MFA and embassy legalization order, plus the 2027 Apostille route.",
        path: "/en/poa",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/poa" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Power of Attorney", url: `${site}/en/poa` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildHowToStepsSchema({
              name: "How to prepare a Thai power of attorney for use at home and abroad",
              description: "The real working order from drafting the powers to destination embassy legalization",
              path: "/en/poa",
              steps: STEPS.map((s, i) => ({ step: i + 1, title: s.title, body: s.body })),
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Power of attorney by destination jurisdiction",
            itemListElement: POA_JURISDICTIONS.map((j, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/en/poa/jurisdiction/${j.slug}`,
              name: j.nameEn,
            })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/poa")) },
      ],
    };
  },
});

function PoaHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content" className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Power of Attorney</span>
        </nav>
        <h1 className="text-3xl font-extrabold md:text-4xl">
          Power of Attorney in Thailand — Forms, Certification and Destination Rules
        </h1>
        <p className="mt-4 max-w-3xl text-muted-foreground" data-speakable="true">
          A workable Thai power of attorney states the powers precisely, uses the form the receiving authority
          mandates such as Land Department form Tor Dor 21, carries stamp duty under the Revenue Department schedule,
          and is signed before a Notarial Services Attorney. For use abroad it then needs MFA legalization at
          THB 200 per stamp standard or THB 400 express, followed by the destination embassy in Bangkok.
        </p>

        <section className="mt-10">
          <h2 className="mb-4 text-xl font-bold">The five working steps</h2>
          <ol className="space-y-3">
            {STEPS.map((s, i) => (
              <li key={s.title} id={`step-${i + 1}`} className="rounded-lg border bg-card p-4">
                <div className="font-semibold">{i + 1}. {s.title}</div>
                <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">Knowledge topics and FAQs</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {POA_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/en/poa/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleEn}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descEn}</p>
              </li>
            ))}
          </ul>
          <a href="/en/poa/faq" className="mt-4 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">
            Browse every question and answer
          </a>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">Power of attorney by destination jurisdiction</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {POA_JURISDICTIONS.map((j) => (
              <a key={j.slug} href={`/en/poa/jurisdiction/${j.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {j.nameEn}
              </a>
            ))}
          </div>
        </section>

        <section className="mt-10 space-y-3">
          <h2 className="text-xl font-bold">Frequently asked questions</h2>
          {faqs.map((f: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 4 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{f.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </section>

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Have the draft checked before you sign</h2>
          <p className="text-sm text-muted-foreground">
            Tell us the transaction, the receiving authority and the destination country — we check the form and the
            legalization order before signing day.
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Talk to our team</a>
        </section>
        <FaqSourceNote topic="poa" locale="en" />
      </main>
      <SiteFooter />
    </div>
  );
}
